import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { buildAdVisitUrl, selectAd } from "@/api/ads";
import { Spinner } from "@/components/ui/spinner";
import { useProtocolShell } from "@/contexts/ProtocolShellContext";

const PLACEHOLDER_HREF = "/account/about#support-card";

function AdWatermark() {
    return (
        <span className="native-v3-ad-watermark absolute left-2 top-2 z-[1] border border-white/15 bg-black/80 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white shadow-sm backdrop-blur-sm">
            Annons
        </span>
    );
}

export default function AdSlot({
    clubSlug,
    awayClubSlug,
    homeTeamId,
    awayTeamId,
    placement = "banner",
    slotKey = "default",
    refreshMs = 30000,
    minSwapMs = 15000,
    timeDrivenRotation = false,
    stableOnceLoaded = false,
    lazy = true,
    hideIfEmpty = true,
    responsive = true,
    maxWidthClasses = "max-w-[320px] sm:max-w-[468px] md:max-w-[728px] lg:max-w-[970px]",
    aspectClasses = "aspect-[320/50] sm:aspect-[468/60] md:aspect-[728/90] lg:aspect-[970/90]",
    width = 320,
    height = 60,
    rounded = 8,
    className = "",
    style = {},
}) {
    const navigate = useNavigate();
    const { hasActiveProtocol, isOpen, minimizeProtocolSession } = useProtocolShell();
    const [ad, setAd] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const wrapRef = useRef(null);
    const timerRef = useRef(null);
    const visibleRef = useRef(!lazy);
    const mountedRef = useRef(true);
    const lastLoadAtRef = useRef(0);
    const ioTickRef = useRef(0);
    const hasLoadedAdRef = useRef(false);
    const adShownAtRef = useRef(0);

    const isLivePlacement = placement === "banner_live";
    const shouldShowPreparedPlaceholder = !isLivePlacement;

    const handlePlaceholderClick = React.useCallback((event) => {
        if (event) {
            event.preventDefault();
        }

        if (hasActiveProtocol && isOpen) {
            minimizeProtocolSession();
            window.setTimeout(() => {
                navigate(PLACEHOLDER_HREF);
            }, 120);
            return;
        }

        navigate(PLACEHOLDER_HREF);
    }, [hasActiveProtocol, isOpen, minimizeProtocolSession, navigate]);

    function canReuseCurrentAd() {
        if (!timeDrivenRotation || !adShownAtRef.current || !hasLoadedAdRef.current) return false;
        return Date.now() - adShownAtRef.current < minSwapMs;
    }

    async function load(force = false) {
        if (!mountedRef.current) return;
        if (lazy && !visibleRef.current) return;
        if (stableOnceLoaded && hasLoadedAdRef.current) return;

        const now = Date.now();
        if (!force && now - lastLoadAtRef.current < minSwapMs) return;

        try {
            setErr(null);
            const res = await selectAd({
                placement,
                slotKey,
                clubSlug,
                awayClubSlug,
                homeTeamId,
                awayTeamId,
            });
            if (!mountedRef.current) return;
            setAd(res?.ad || null);
            if (res?.ad) {
                hasLoadedAdRef.current = true;
                adShownAtRef.current = Date.now();
            } else {
                hasLoadedAdRef.current = false;
                adShownAtRef.current = 0;
            }
            lastLoadAtRef.current = Date.now();
        } catch (e) {
            if (!mountedRef.current) return;
            setErr(e?.message || "Kunde inte hämta annons");
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }

    function startInterval() {
        if (timerRef.current || refreshMs <= 0 || stableOnceLoaded) return;
        timerRef.current = setInterval(() => load(false), refreshMs);
    }

    function stopInterval() {
        if (!timerRef.current) return;
        clearInterval(timerRef.current);
        timerRef.current = null;
    }

    useEffect(() => {
        mountedRef.current = true;

        if (!lazy) {
            load(true);
            startInterval();
            return () => {
                mountedRef.current = false;
                stopInterval();
            };
        }

        const el = wrapRef.current;
        if (!el) return undefined;

        const io = new IntersectionObserver(
            (entries) => {
                const now = Date.now();
                if (now - ioTickRef.current < 100) return;
                ioTickRef.current = now;

                const isVisible = entries.some((entry) => entry.isIntersecting);
                visibleRef.current = isVisible;

                if (isVisible) {
                    if (!canReuseCurrentAd() && !(stableOnceLoaded && hasLoadedAdRef.current)) {
                        load(true);
                    }
                    startInterval();
                } else {
                    stopInterval();
                }
            },
            { rootMargin: "100px 0px" }
        );

        io.observe(el);

        const kickstartId = window.setTimeout(() => {
            if (!mountedRef.current || !wrapRef.current) return;
            const rect = wrapRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
            const isVisible =
                rect.bottom >= 0 &&
                rect.right >= 0 &&
                rect.top <= viewportHeight &&
                rect.left <= viewportWidth;

            if (isVisible) {
                visibleRef.current = true;
                if (!canReuseCurrentAd() && !(stableOnceLoaded && hasLoadedAdRef.current)) {
                    load(true);
                }
                startInterval();
            }
        }, 120);

        return () => {
            mountedRef.current = false;
            stopInterval();
            window.clearTimeout(kickstartId);
            try {
                io.disconnect();
            } catch {
                // ignore
            }
        };
    }, [clubSlug, homeTeamId, awayTeamId, placement, slotKey, lazy, refreshMs, minSwapMs, stableOnceLoaded, timeDrivenRotation]);

    const responsiveLoading = (
        <div className={`w-full mx-auto ${maxWidthClasses}`}>
            <div className={`${aspectClasses} grid place-items-center`}>
                <Spinner size="lg" className="text-zinc-300" />
            </div>
        </div>
    );

    const responsiveEmpty = (
        <div className={`native-v3-ad-slot w-full mx-auto ${maxWidthClasses}`}>
            <a href={PLACEHOLDER_HREF} aria-label="Läs mer om annonsplats hos Lapstr" onClick={handlePlaceholderClick}>
                <div className={`${aspectClasses} native-v3-ad-placeholder relative overflow-hidden border border-primary/35 transition-transform duration-200`}>
                    <AdWatermark />
                    <div className="native-v3-ad-accent absolute inset-y-0 left-0 w-1.5 bg-primary/75" />
                    <div className="native-v3-ad-lines absolute inset-0" />
                    <div className="native-v3-ad-copy relative flex h-full flex-col items-center justify-center px-5 text-center">
                        <div className="native-v3-ad-kicker border border-primary/30 bg-background/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">
                            Ledig annonsyta
                        </div>
                        <div className="native-v3-ad-title mt-2 text-base font-semibold text-foreground sm:text-[1.05rem]">Vill du synas här?</div>
                        <div className="native-v3-ad-description mt-1 max-w-[34rem] text-[11px] leading-5 text-muted-foreground sm:text-xs">
                            {err ? "Annonsytan är tillfälligt tom." : "Den här annonsytan är reserverad för annonser i appen."}
                        </div>
                    </div>
                </div>
            </a>
        </div>
    );

    const fixedLoading = (
        <div
            className="grid place-items-center"
            style={{ width, height, borderRadius: rounded }}
            aria-label="Laddar annons"
        >
            <Spinner size="md" className="text-zinc-300" />
        </div>
    );

    const fixedEmpty = (
        <a
            href={PLACEHOLDER_HREF}
            aria-label="Läs mer om annonsplats hos Lapstr"
            onClick={handlePlaceholderClick}
            className={className}
            style={{ display: "inline-block", ...style }}
        >
            <div
                className="native-v3-ad-placeholder relative overflow-hidden border border-primary/35 transition-transform duration-200"
                style={{ width, height, borderRadius: rounded }}
            >
                <AdWatermark />
                <div className="native-v3-ad-accent absolute inset-y-0 left-0 w-1.5 bg-primary/75" />
                <div className="native-v3-ad-lines absolute inset-0" />
                <div className="native-v3-ad-copy relative flex h-full flex-col items-center justify-center px-4 text-center">
                    <div className="native-v3-ad-kicker border border-primary/30 bg-background/70 px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-primary">
                        Ledig annonsyta
                    </div>
                    <div className="native-v3-ad-title mt-2 text-sm font-semibold text-foreground">Vill du synas här?</div>
                    <div className="native-v3-ad-description mt-1 text-[11px] leading-4 text-muted-foreground">
                        {err ? "Annonsytan är tillfälligt tom." : "Annonsplats i Lapstr"}
                    </div>
                </div>
            </div>
        </a>
    );

    if (loading) {
        return responsive
            ? <div ref={wrapRef} className={className} style={style}>{responsiveLoading}</div>
            : <div ref={wrapRef} className={className} style={{ display: "inline-block", ...style }}>{fixedLoading}</div>;
    }

    if (!ad) {
        if (hideIfEmpty && !shouldShowPreparedPlaceholder) {
            return <div ref={wrapRef} className={className} style={style} />;
        }
        return responsive
            ? <div ref={wrapRef} className={className} style={style}>{responsiveEmpty}</div>
            : <div ref={wrapRef} className={className} style={{ display: "inline-block", ...style }}>{fixedEmpty}</div>;
    }

    const href = buildAdVisitUrl(ad, placement, slotKey);

    if (responsive) {
        return (
            <div ref={wrapRef} className={`w-full ${maxWidthClasses} ${className}`} style={style}>
                <div className={`${aspectClasses} native-v3-ad-media relative overflow-hidden`}>
                    <AdWatermark />
                    <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={ad.title || "Annons"}
                        className="block w-full h-full"
                    >
                        <img
                            src={ad.image_url}
                            alt={ad.title || "Annons"}
                            className="w-full h-full object-contain"
                            loading="eager"
                            decoding="async"
                        />
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div ref={wrapRef} className={className} style={{ display: "inline-block", ...style }}>
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={ad.title || "Annons"}
                style={{ display: "inline-block", width, height, position: "relative" }}
            >
                <AdWatermark />
                <img
                    className="native-v3-ad-media"
                    src={ad.image_url}
                    alt={ad.title || "Annons"}
                    width={width}
                    height={height}
                    style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: rounded }}
                    loading="eager"
                    decoding="async"
                />
            </a>
        </div>
    );
}
